import Editor from "@monaco-editor/react";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import { Box, Stack, Typography } from "@mui/material";
import { defaultOverrides, MuiMarkdown } from "mui-markdown";
import { Children, isValidElement, type ReactNode, useState } from "react";
import { MARKDOWN_COLORS } from "@/theme/theme";

// Map common language aliases to Monaco language IDs
const languageMap: Record<string, string> = {
  js: "javascript",
  ts: "typescript",
  typescript: "typescript",
  javascript: "javascript",
  python: "python",
  tsx: "typescript",
  jsx: "javascript",
  py: "python",
  rb: "ruby",
  yml: "yaml",
  sh: "shell",
  bash: "shell",
  zsh: "shell",
  md: "markdown",
  json: "json",
  html: "html",
  css: "css",
  scss: "scss",
  sql: "sql",
  graphql: "graphql",
  dockerfile: "dockerfile",
  xml: "xml",
  java: "java",
  c: "c",
  cpp: "cpp",
  "c++": "cpp",
  csharp: "csharp",
  "c#": "csharp",
  go: "go",
  rust: "rust",
  swift: "swift",
  kotlin: "kotlin",
  php: "php",
  perl: "perl",
  r: "r",
  scala: "scala",
  haskell: "haskell",
  lua: "lua",
  elixir: "elixir",
  clojure: "clojure",
  fsharp: "fsharp",
  "f#": "fsharp",
};

const getMonacoLanguage = (className?: string): string => {
  if (!className) return "plaintext";
  // lang-<name> or language-<name>
  const match = className.match(/(?:lang|language)-(\w+)/);
  if (!match) return "plaintext";
  const lang = match[1].toLowerCase();
  return languageMap[lang] || lang;
};

const extractCodeContent = (children: ReactNode): { code: string; language: string } => {
  let code = "";
  let language = "plaintext";

  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === "code") {
      const codeProps = child.props as {
        className?: string;
        children?: ReactNode;
      };
      language = getMonacoLanguage(codeProps.className);
      // Extract text content from code element
      const extractText = (node: ReactNode): string => {
        if (typeof node === "string") return node;
        if (typeof node === "number") return String(node);
        if (Array.isArray(node)) return node.map(extractText).join("");
        if (isValidElement(node)) {
          const props = node.props as { children?: ReactNode };
          return extractText(props.children);
        }
        return "";
      };
      code = extractText(codeProps.children);
    } else if (typeof child === "string") {
      code = child;
    }
  });

  return { code: code.trim(), language };
};

// Video component with proper containment
const MarkdownVideo = (props: React.VideoHTMLAttributes<HTMLVideoElement>) => {
  return (
    <Box
      component="video"
      {...props}
      controls
      sx={{
        maxWidth: "720px",
        width: "100%",
        height: "auto",
        display: "block",
        my: 2,
        mx: "auto",
        borderRadius: "0.5rem",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        border: "1px solid",
        borderColor: "divider",
      }}
    />
  );
};

// Image component with proper containment
const MarkdownImage = (props: React.ImgHTMLAttributes<HTMLImageElement>) => {
  const [errored, setErrored] = useState(false);

  if (errored || !props.src) {
    return (
      <Box
        sx={{
          display: "inline-flex",
          maxWidth: "100%",
          my: 2,
          padding: 1.25,
          borderRadius: "0.5rem",
          border: "1px dashed",
          borderColor: "divider",
          backgroundColor: "action.hover",
          color: "text.secondary",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <BrokenImageIcon fontSize="small" />
          <Typography variant="caption" noWrap>
            {props.alt || "Asset unavailable"}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      component="img"
      {...props}
      onError={() => setErrored(true)}
      sx={{
        maxWidth: "720px",
        width: "100%",
        height: "auto",
        display: "block",
        my: 2,
        mx: "auto",
        borderRadius: "0.5rem",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
      }}
    />
  );
};

const MonacoCodeBlock = ({ children }: { children: ReactNode }) => {
  const { code, language } = extractCodeContent(children);
  const lineCount = code.split("\n").length;
  const height = Math.min(Math.max(lineCount * 19 + 16, 60), 400);

  console.log({
    code,
    language,
    children,
  });

  return (
    <Box
      sx={{
        my: 2,
        borderRadius: "0.5rem",
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Editor
        height={height}
        language={language}
        value={code}
        theme="vs"
        options={{
          readOnly: true,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          lineNumbers: "on",
          renderLineHighlight: "none",
          folding: false,
          overviewRulerLanes: 0,
          hideCursorInOverviewRuler: true,
          overviewRulerBorder: false,
          contextmenu: false,
          domReadOnly: true,
        }}
      />
    </Box>
  );
};

export const MarkdownBlock = ({ content }: { content: string }) => {
  // Process content to handle [text] lines as h7 headers
  const processContent = (text: string) => {
    const lines = text.split("\n");
    const processedLines = lines.map((line, index) => {
      // Check if this is the first line and starts with [text] pattern
      if (index === 0 && line.match(/^\[.*?\]/)) {
        const match = line.match(/^\[(.*?)\]\s*(.*)$/);
        if (match) {
          const [, bracketText, restText] = match;
          return `###### ${bracketText}: ${restText}`;
        }
      }
      return line;
    });
    return processedLines.join("\n");
  };

  const processedContent = processContent(content);

  return (
    <Box
      sx={{
        "& .MuiTypography-root": {
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
        },
        "& ul li::before": {
          content: '"•"',
          color: "text.secondary",
          fontWeight: "bold",
          position: "absolute",
          left: "-0.5rem",
          top: "0",
        },
        "& ol li::before": {
          content: 'counter(list-counter) "."',
          counterIncrement: "list-counter",
          color: "text.secondary",
          fontWeight: "600",
          position: "absolute",
          left: "-0.5rem",
          top: "0",
          fontSize: "0.8em",
        },
        "& ol": {
          counterReset: "list-counter",
        },
        // h7 styling for the processed [text] headers
        "& .h7-header": {
          marginBottom: "0.75rem",
          fontSize: "0.8rem",
          fontWeight: "500",
          color: "text.secondary",
          letterSpacing: "0.05em",
        },
      }}
    >
      <MuiMarkdown
        overrides={{
          ...defaultOverrides,
          h1: {
            component: Typography,
            props: {
              variant: "h4",
              sx: {
                fontWeight: 600,
                fontSize: "1.5rem",
                lineHeight: 1.3,
                color: "text.primary",
                marginTop: "2rem",
                marginBottom: "1rem",
                letterSpacing: "-0.01em",
              },
            },
          },
          h2: {
            component: Typography,
            props: {
              variant: "h5",
              sx: {
                fontWeight: 600,
                fontSize: "1.25rem",
                lineHeight: 1.4,
                color: "text.primary",
                marginTop: "1.75rem",
                marginBottom: "0.75rem",
                letterSpacing: "-0.005em",
              },
            },
          },
          h3: {
            component: Typography,
            props: {
              variant: "h6",
              sx: {
                fontWeight: 600,
                fontSize: "1.125rem",
                lineHeight: 1.4,
                color: "text.primary",
                marginTop: "1.5rem",
                marginBottom: "0.5rem",
                letterSpacing: "-0.005em",
              },
            },
          },
          h4: {
            component: Typography,
            props: {
              variant: "subtitle1",
              sx: {
                fontWeight: 600,
                fontSize: "1rem",
                lineHeight: 1.4,
                color: "text.primary",
                marginTop: "1.25rem",
                marginBottom: "0.5rem",
                letterSpacing: "-0.005em",
              },
            },
          },
          h5: {
            component: Typography,
            props: {
              variant: "subtitle2",
              sx: {
                fontWeight: 600,
                fontSize: "0.875rem",
                lineHeight: 1.4,
                color: "text.primary",
                marginTop: "1rem",
                marginBottom: "0.5rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              },
            },
          },
          h6: {
            component: Typography,
            props: {
              variant: "body1",
              sx: {
                fontWeight: 500,
                fontSize: "0.8rem",
                lineHeight: 1.4,
                color: "text.secondary",
                marginTop: "0.5rem",
                marginBottom: "0.75rem",
                letterSpacing: "0.05em",
              },
            },
          },
          p: {
            component: Typography,
            props: {
              variant: "body2",
              sx: {
                fontSize: "0.95rem",
                lineHeight: 1.6,
                color: "text.primary",
                marginTop: "0.75rem",
                marginBottom: "0.75rem",
                whiteSpace: "pre-wrap",
                letterSpacing: "-0.01em",
              },
            },
          },
          blockquote: {
            component: Box,
            props: {
              sx: {
                borderLeft: "3px solid",
                borderLeftColor: "divider",
                paddingLeft: "1rem",
                marginLeft: "0",
                marginTop: "1rem",
                marginBottom: "1rem",
                backgroundColor: "grey.50",
                padding: "0.75rem 1rem",
                borderRadius: "0.375rem",
                fontStyle: "italic",
                color: "text.secondary",
                fontSize: "0.9rem",
                lineHeight: 1.6,
              },
            },
          },
          ul: {
            component: "ul",
            props: {
              style: {
                paddingLeft: "1.25rem",
                marginTop: "0.75rem",
                marginBottom: "0.75rem",
                listStyle: "none",
              },
            },
          },
          ol: {
            component: "ol",
            props: {
              style: {
                paddingLeft: "1.25rem",
                marginTop: "0.75rem",
                marginBottom: "0.75rem",
                listStyle: "none",
                counterReset: "list-counter",
              },
            },
          },
          li: {
            component: "li",
            props: {
              style: {
                marginBottom: "0.5rem",
                position: "relative",
                paddingLeft: "1rem",
                fontSize: "0.95rem",
                lineHeight: 1.6,
                color: MARKDOWN_COLORS.text,
              },
            },
          },
          a: {
            component: "a",
            props: {
              target: "_blank",
              rel: "noopener noreferrer",
              style: {
                color: MARKDOWN_COLORS.link,
                textDecoration: "underline",
                textUnderlineOffset: "2px",
                fontWeight: "500",
              },
            },
          },
          img: {
            component: MarkdownImage,
          },
          video: {
            component: MarkdownVideo,
          },
          table: {
            component: "table",
            props: {
              style: {
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "1rem",
                marginBottom: "1rem",
                fontSize: "0.875rem",
                backgroundColor: MARKDOWN_COLORS.tableBg,
                borderRadius: "0.5rem",
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              },
            },
          },
          th: {
            component: "th",
            props: {
              style: {
                backgroundColor: MARKDOWN_COLORS.tableHeaderBg,
                borderBottom: `1px solid ${MARKDOWN_COLORS.tableBorder}`,
                padding: "0.75rem 1rem",
                textAlign: "left",
                fontWeight: "600",
                fontSize: "0.8rem",
                color: MARKDOWN_COLORS.tableHeaderText,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              },
            },
          },
          td: {
            component: "td",
            props: {
              style: {
                borderBottom: `1px solid ${MARKDOWN_COLORS.tableCellBorder}`,
                padding: "0.75rem 1rem",
                color: MARKDOWN_COLORS.text,
              },
            },
          },
          hr: {
            component: "hr",
            props: {
              style: {
                border: "none",
                borderTop: `1px solid ${MARKDOWN_COLORS.tableBorder}`,
                margin: "2rem 0",
                height: "1px",
              },
            },
          },
          code: {
            component: "code",
            props: {
              style: {
                backgroundColor: MARKDOWN_COLORS.inlineCodeBg,
                color: MARKDOWN_COLORS.inlineCode,
                padding: "0.125rem 0.375rem",
                borderRadius: "0.25rem",
                fontSize: "0.85em",
                fontFamily:
                  "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Courier New', monospace",
                fontWeight: "500",
                border: `1px solid ${MARKDOWN_COLORS.inlineCodeBorder}`,
              },
            },
          },
          pre: {
            component: MonacoCodeBlock,
          },
        }}
      >
        {processedContent}
      </MuiMarkdown>
    </Box>
  );
};
